import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaPaperPlane, FaSpinner, FaThumbtack } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import ChartEditor from './ChartEditor';
import './AgentVChatOverlay.css';
import veloxIcon from '../../assets/Velox-icon-b&w.png';

const PRIMARY_COLOR = '#233e80';
const SECONDARY_PRIMARY_COLOR = '#1a6ed8';
const COLORS = [PRIMARY_COLOR, SECONDARY_PRIMARY_COLOR, '#3b9efc', '#1f5bd8', '#2563eb', '#0f2d73'];

export default function AgentVChatOverlay({ isOpen, onClose, onChartGenerated }) {
  const [messages, setMessages] = useState([
    {
      type: 'assistant',
      content: 'Hello! I\'m Agent V, your chart generation assistant. Describe the chart you\'d like to create based on your Velox AI data, and I\'ll generate it for you. For example:\n\n- "Create a bar chart showing companies by deal stage"\n- "Generate a pie chart of companies by industry"\n- "Show me a line chart of transcript uploads over time"'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pinningChartId, setPinningChartId] = useState(null);
  const [editingChartIndex, setEditingChartIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch('/api/wizard/generate-chart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate chart');
      }

      const data = await response.json();
      
      // Add assistant response
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: data.message,
        chartData: data.chartData 
      }]);

      // Don't automatically add chart to main screen - user must pin it
    } catch (error) {
      console.error('Error generating chart:', error);
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: 'Sorry, I encountered an error generating the chart. Please try again with a different description.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinChart = async (chartData, messageIndex) => {
    if (!chartData || !onChartGenerated) return;
    
    // Set pinning state for this specific chart
    const chartId = `chart-${messageIndex}`;
    setPinningChartId(chartId);
    
    try {
      await onChartGenerated(chartData);
    } catch (error) {
      console.error('Error pinning chart:', error);
    } finally {
      setPinningChartId(null);
    }
  };

  const handleChartClick = (messageIndex) => {
    setEditingChartIndex(messageIndex);
  };

  const handleEditorSave = (updatedChartData, chartId) => {
    // Update the message with the edited chart data
    setMessages(prev => prev.map((msg, idx) => 
      idx === editingChartIndex 
        ? { ...msg, chartData: updatedChartData }
        : msg
    ));
    setEditingChartIndex(null);
  };

  const renderChart = (chartData, chartIndex) => {
    if (!chartData) return null;

    const { type, data, config = {} } = chartData;
    const { xKey = 'name', yKey = 'value', title = 'Chart' } = config;

    const chartHeight = 300;
    const gradientId = `colorGradient-${chartIndex || Date.now()}`;

    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey={xKey} stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              <Legend />
              <Bar dataKey={yKey} fill={PRIMARY_COLOR} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey={xKey} stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={yKey} 
                stroke={PRIMARY_COLOR} 
                strokeWidth={3}
                dot={{ fill: PRIMARY_COLOR, r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey={yKey}
              >
                {data.map((entry, idx) => (
                <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey={xKey} stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey={yKey} 
                stroke={PRIMARY_COLOR} 
                fill={`url(#${gradientId})`}
                strokeWidth={2}
              />
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={PRIMARY_COLOR} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={PRIMARY_COLOR} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return <div>Unsupported chart type: {type}</div>;
    }
  };

  const renderMessage = (message, index) => {
    return (
      <div
        key={index}
        className={`agent-v-message ${message.type === 'user' ? 'agent-v-message-user' : 'agent-v-message-assistant'}`}
      >
        <div className={`agent-v-message-bubble ${message.type === 'user' ? 'agent-v-bubble-user' : 'agent-v-bubble-assistant'}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {message.content}
          </ReactMarkdown>
          
          {/* Render chart if present */}
          {message.chartData && (
            <div className="agent-v-chart-container">
              <div className="agent-v-chart-header">
                <h4 className="agent-v-chart-title">
                  {message.chartData.config?.title || 'Chart'}
                </h4>
                <button
                  className="agent-v-pin-button"
                  onClick={() => handlePinChart(message.chartData, index)}
                  disabled={pinningChartId === `chart-${index}`}
                  title={pinningChartId === `chart-${index}` ? "Saving chart..." : "Pin chart to dashboard"}
                >
                  {pinningChartId === `chart-${index}` ? (
                    <FaSpinner className="agent-v-spinner" />
                  ) : (
                    <FaThumbtack />
                  )}
                </button>
              </div>
              <div 
                className="agent-v-chart-content" 
                onClick={() => handleChartClick(index)}
                style={{ cursor: 'pointer' }}
              >
                {renderChart(message.chartData, index)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="agent-v-overlay">
      <div className="agent-v-overlay-backdrop" onClick={onClose} />
      <div className="agent-v-overlay-container">
        {/* Header */}
        <div className="agent-v-overlay-header">
          <div className="agent-v-overlay-title">
            <img src={veloxIcon} alt="Velox AI" className="chat-icon" />
            <span>Agent V</span>
          </div>
          <button className="agent-v-overlay-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Messages Container */}
        <div className="agent-v-messages-container" ref={chatContainerRef}>
          {messages.map((message, index) => renderMessage(message, index))}
          {isLoading && (
            <div className="agent-v-message agent-v-message-assistant">
              <div className="agent-v-message-bubble agent-v-bubble-assistant">
                <FaSpinner className="agent-v-spinner" />
                <span style={{ marginLeft: '8px' }}>Generating chart...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chart Editor for chat charts */}
        {editingChartIndex !== null && messages[editingChartIndex]?.chartData && (
          <ChartEditor
            chartData={messages[editingChartIndex].chartData}
            chartId={null}
            isOpen={editingChartIndex !== null}
            onClose={() => setEditingChartIndex(null)}
            onSave={handleEditorSave}
          />
        )}

        {/* Input Area */}
        <form className="agent-v-input-container" onSubmit={handleSubmit}>
          <input
            type="text"
            className="agent-v-input"
            placeholder="Describe the chart you want to create..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
          />
          <button
            type="submit"
            className="agent-v-send-button"
            disabled={!input.trim() || isLoading}
          >
            <FaPaperPlane />
          </button>
        </form>
      </div>
    </div>
  );
}

